import { Box, Button, TextField, Typography } from "@mui/material";
import type {
  SecurityOutcome,
  SecuritySeverity,
  SecuritySource,
} from "@xop/dashboard-shared";
import { SECURITY_SOURCES } from "@xop/dashboard-shared";
import type { JSX } from "react";

const WINDOWS: { label: string; hours: number }[] = [
  { label: "Last hour", hours: 1 },
  { label: "Last 24 hours", hours: 24 },
  { label: "Last 7 days", hours: 24 * 7 },
  { label: "Last 30 days", hours: 24 * 30 },
];

const SEVERITY_FILTERS: { label: string; value: "" | SecuritySeverity }[] = [
  { label: "All severities", value: "" },
  { label: "Info and up", value: "info" },
  { label: "Warning and up", value: "warn" },
  { label: "Critical only", value: "critical" },
];

const OUTCOME_FILTERS: { label: string; value: "" | SecurityOutcome }[] = [
  { label: "All outcomes", value: "" },
  { label: "Successful access", value: "success" },
  { label: "Denied", value: "denied" },
  { label: "Failed", value: "failed" },
];

/** Native <select> styled to match the prior themed control (kept native so
 * the filter tests can drive it via fireEvent.change on a labelled select). */
const selectSx = {
  borderRadius: 1,
  border: 1,
  borderColor: "divider",
  bgcolor: "background.paper",
  color: "text.primary",
  px: 1.5,
  py: 0.75,
  fontSize: 14,
  cursor: "pointer",
  "&:hover": { borderColor: "text.disabled" },
} as const;

/**
 * The Security console's filter/refresh bar — time window, severity, outcome,
 * source (server-side filters) + a client-side actor search and a manual
 * refresh, with the "updated … · auto every 30s" stamp. Extracted from
 * `SecurityPanel.tsx`; the native selects keep their `aria-label`s so the
 * integration tests can drive them.
 */
export function SecurityControls({
  hours,
  onHours,
  minSeverity,
  onMinSeverity,
  outcome,
  onOutcome,
  source,
  onSource,
  actorQuery,
  onActorQuery,
  loading,
  generatedAt,
  onRefresh,
}: {
  hours: number;
  onHours: (n: number) => void;
  minSeverity: "" | SecuritySeverity;
  onMinSeverity: (v: "" | SecuritySeverity) => void;
  outcome: "" | SecurityOutcome;
  onOutcome: (v: "" | SecurityOutcome) => void;
  source: "" | SecuritySource;
  onSource: (v: "" | SecuritySource) => void;
  actorQuery: string;
  onActorQuery: (v: string) => void;
  loading: boolean;
  generatedAt: string | null;
  onRefresh: () => void;
}): JSX.Element {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
      <Box
        component="select"
        aria-label="Time window"
        value={hours}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onHours(Number(e.target.value))}
        sx={selectSx}
      >
        {WINDOWS.map((w) => (
          <option key={w.hours} value={w.hours}>
            {w.label}
          </option>
        ))}
      </Box>
      <Box
        component="select"
        aria-label="Minimum severity"
        value={minSeverity}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          onMinSeverity(e.target.value as "" | SecuritySeverity)
        }
        sx={selectSx}
      >
        {SEVERITY_FILTERS.map((f) => (
          <option key={f.value || "all"} value={f.value}>
            {f.label}
          </option>
        ))}
      </Box>
      <Box
        component="select"
        aria-label="Outcome"
        value={outcome}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          onOutcome(e.target.value as "" | SecurityOutcome)
        }
        sx={selectSx}
      >
        {OUTCOME_FILTERS.map((f) => (
          <option key={f.value || "all"} value={f.value}>
            {f.label}
          </option>
        ))}
      </Box>
      <Box
        component="select"
        aria-label="Source"
        value={source}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          onSource(e.target.value as "" | SecuritySource)
        }
        sx={selectSx}
      >
        <option value="">All sources</option>
        {SECURITY_SOURCES.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </Box>
      <TextField
        inputProps={{ "aria-label": "Search by actor, action, IP" }}
        placeholder="Search actor / action / IP…"
        value={actorQuery}
        onChange={(e) => onActorQuery(e.target.value)}
        sx={{ width: 224 }}
      />
      <Button
        variant="outlined"
        color="inherit"
        size="small"
        onClick={onRefresh}
        disabled={loading}
      >
        {loading ? "Refreshing…" : "Refresh"}
      </Button>
      {generatedAt && (
        <Typography component="span" sx={{ fontSize: 12, color: "text.disabled" }}>
          Updated {new Date(generatedAt).toLocaleTimeString()} · auto every 30s
        </Typography>
      )}
    </Box>
  );
}
